"use client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { IPages, IUserData } from "@/types";
import {
    IconAlertCircle,
    IconArrowDown,
    IconArrowUp,
    IconGripVertical,
    IconPlus,
    IconStack2,
} from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";
import {
    Popover,
    PopoverAnchor,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { UserURL } from "@/functions";
import { toast } from "sonner";
import { RevalidateLink } from "../layout/RevalidateLink";
import { EditPagePopover } from "../profile/EditPagePopover";
interface Props {
    user?: IUserData;
    pages: IPages[];
    impersonate?: boolean;
    setNewPages: React.Dispatch<React.SetStateAction<IPages[]>>;
}
export default function PagesDir({
    user,
    pages,
    impersonate,
    setNewPages,
}: Props) {
    const [isExpanded, setIsExpanded] = useState(false);
    useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != "-2") {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: "-2",
                },
            })
        );
        if (!isExpanded) {
            const event = new CustomEvent("expandBlock", {
                detail: {
                    content: (
                        <PagesPopout
                            user={user || null}
                            pages={pages}
                            impersonate={impersonate}
                            setNewPages={setNewPages}
                        />
                    ),
                    title: "Your Pages",
                    description: "Create, move, or edit your pages",
                },
            });
            window.dispatchEvent(event);
            setIsExpanded(true);
        } else {
            const event = new CustomEvent("collapseBlock");
            window.dispatchEvent(event);
            setIsExpanded(false);
        }
    };
    useEffect(() => {
        if (isExpanded) {
            const event = new CustomEvent("expandBlock", {
                detail: {
                    content: (
                        <PagesPopout
                            user={user || null}
                            pages={pages}
                            impersonate={impersonate}
                            setNewPages={setNewPages}
                        />
                    ),
                    title: "Your Pages",
                    description: "Create, move, or edit your pages",
                },
            });
            window.dispatchEvent(event);
        }
    }, [impersonate, user, setNewPages, pages]);

    return (
        <Button
            className={`group relative flex h-auto shrink-0 gap-2 rounded-md bg-muted text-sm text-foreground transition-all duration-300 hover:bg-primary hover:text-primary-foreground ${isExpanded ? "bg-primary text-primary-foreground" : "bg-muted"}`}
            // size="icon"
            onClick={handleExpand}
        >
            <IconStack2 size={16} />
            <span className="hidden md:block">Pages</span>
            {/* <span className="hidden md:block">Pages</span> */}
        </Button>
    );
}

const PagesPopout = ({
    user,
    pages,
    impersonate,
    setNewPages,
}: {
    user: IUserData | null;
    pages: IPages[];
    impersonate?: boolean;
    setNewPages: React.Dispatch<React.SetStateAction<IPages[]>>;
}) => {
    const [pageName, setPageName] = useState("");
    const [pageUrl, setPageUrl] = useState("");
    const pathname = usePathname();
    if (!user) return null;
    const movePage = async (index: number, direction: "up" | "down") => {
        const _newPages = [...pages];
        const [movedPage] = _newPages.splice(index, 1);
        if (direction === "up") {
            _newPages.splice(index - 1, 0, movedPage);
        } else {
            _newPages.splice(index + 1, 0, movedPage);
        }
        _newPages.forEach((page, index) => {
            page.sort = index;
        });
        setNewPages(_newPages);
        const loading = toast.loading("Updating page order");
        try {
            const res = await fetch("/api/admin/pages/move", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    pages: _newPages.map((page) => ({
                        id: page.id,
                        sort: page.sort,
                    })), // Only sending necessary properties
                    ids: _newPages.map((page) => page.id), // Array of page IDs to update
                    impersonate: impersonate ? user.id : null,
                }),
            });
            const data = await res.json();
            if (!data.error) {
                toast.success("Pages moved successfully!", { id: loading });
            } else {
                toast.error(
                    "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                    {
                        description: JSON.stringify(data.error),
                        id: loading,
                    }
                );
            }
        } catch (error) {
            toast.error("Failed to move pages. Please try again later.", {
                id: loading,
            });
        }
    };
    const makeNewPage = async () => {
        if (pages.find((page) => page.slug == pageUrl)) {
            alert("Page already exists");
            return;
        }
        toast("Creating page...");
        const res = await fetch("/api/admin/pages/create", {
            method: "POST",
            body: JSON.stringify({
                name: pageName,
                url: pageUrl,
                sort: pages.length,
                impersonate: impersonate ? user.id : null,
                visible: true,
            }),
        });
        const data = await res.json();
        if (!data.error) {
            // Update the user object directly
            setNewPages([...pages, data.data]);
            setPageName("");
            setPageUrl("");
            toast.success("Updated");
            window.location.href = "/admin/content/" + data.data.slug;
        } else {
            if (data?.error == "page slug not allowed") {
                toast.error("page slug not allowed");
            } else if (data?.error == "page slug already taken") {
                toast.error("page slug already taken");
            } else if (data?.error == "page slug too short") {
                toast.error("page slug too short");
            } else {
                toast.error(
                    "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                    {
                        description: JSON.stringify(data),
                    }
                );
            }
        }
    };
    const makeNewPageBlog = async () => {
        if (pages.find((page) => page.slug == pageUrl)) {
            alert("Page already exists");
            return;
        }
        toast("Creating page...");
        const res = await fetch("/api/admin/pages/create", {
            method: "POST",
            body: JSON.stringify({
                name: pageName,
                url: pageUrl,
                sort: pages.length,
                impersonate: impersonate ? user.id : null,
                visible: false,
            }),
        });
        const data = await res.json();
        if (!data.error) {
            // Update the user object directly
            setNewPages([...pages, data.data]);
            setPageName("");
            setPageUrl("");
            toast.success("Updated");
        } else {
            if (data?.error == "page slug not allowed") {
                toast.error("page slug not allowed");
            } else if (data?.error == "page slug already taken") {
                toast.error("page slug already taken");
            } else if (data?.error == "page slug too short") {
                toast.error("page slug too short");
            } else {
                toast.error(
                    "Something went wrong, refresh and try again. If the issue persists, please log out and log back in",
                    {
                        description: JSON.stringify(data),
                    }
                );
            }
        }
    };
    return (
        <div className="z-10 space-y-2 pt-3">
            <div className="flex items-center gap-1">
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            size="lg"
                            className="relative w-full text-lg font-bold"
                            disabled={
                                (user.account_type == "user" &&
                                    pages.length >= 3) ||
                                (user.account_type == "bfree" &&
                                    pages.length >= 3) ||
                                (user.account_type == "bplus" &&
                                    pages.length >= 10)
                            }
                        >
                            <IconPlus /> New Page
                            <PopoverAnchor>
                                <div className="absolute inset-y-3 right-3 w-full" />
                            </PopoverAnchor>
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent
                        sideOffset={20}
                        className="z-[999999] flex w-full min-w-40 flex-col gap-3 bg-popover"
                    >
                        <Label variant="headingtoggle">Create</Label>
                        <Separator />
                        <Input
                            label="Name"
                            className="text-copy bg-muted"
                            variant="card"
                            onChange={(e) => setPageName(e.target.value)}
                            value={pageName}
                        />
                        {/* I think we could do some "slugification" instead, so if you page is called "My Page" we can auto do the slug as /my-page as default? */}
                        <div className="flex items-center">
                            <p className="text-copy text-nowrap rounded-l-md bg-primary py-2 pl-2 pr-0.5">
                                {UserURL(user)}/
                            </p>
                            <Input
                                variant="card"
                                className="text-copy rounded-l-none bg-muted pl-0.5"
                                onChange={(e) =>
                                    setPageUrl(
                                        e.target.value
                                            .replaceAll(" ", "-")
                                            .replaceAll("/", "-")
                                            .toLowerCase()
                                    )
                                }
                                value={pageUrl}
                            />
                        </div>
                        {pageUrl === "blog" && (
                            <div className="flex flex-col gap-1 rounded-md border bg-accent p-2">
                                <Label variant={"toggle"}>Woah woah</Label>
                                <Label variant={"subtoggle"}>
                                    This is a special route that automatically
                                    hosts your your blog. Creating a page with
                                    the same route will override this.
                                </Label>
                                <Label variant={"subtoggle"}>
                                    Click "Override" to create the page, or
                                    click "Add to navigation" to automatically
                                    add your blog page to your nav bar.
                                </Label>
                            </div>
                        )}
                        {pageUrl === "blog" ? (
                            <div className="ml-auto flex items-center gap-1">
                                <Button
                                    className="w-fit"
                                    variant={"outline"}
                                    onClick={makeNewPage}
                                >
                                    <IconAlertCircle size={18} /> Override
                                </Button>
                                <Button
                                    className="w-fit"
                                    onClick={makeNewPageBlog}
                                >
                                    <IconPlus size={18} /> Add to navigation
                                </Button>
                            </div>
                        ) : (
                            <Button
                                className="ml-auto w-fit"
                                onClick={makeNewPage}
                            >
                                <IconPlus size={18} /> Create
                            </Button>
                        )}
                    </PopoverContent>
                </Popover>
            </div>
            <Separator />
            <div
                className={`group relative z-20 flex items-center gap-2 rounded-md border bg-card p-2 transition-all duration-200 hover:bg-muted 
                    ${pathname == `/admin/content` ? "border-primary" : ""}
                    `}
            >
                {/* <IconGripVertical className="handle invisible shrink-0 cursor-grab" /> */}
                <RevalidateLink
                    href={`/admin/content`}
                    className={`relative w-full`}
                >
                    <div className="flex flex-col gap-1">
                        <Label
                            variant="toggle"
                            className="mb-0 flex cursor-pointer items-center pb-0 text-base font-bold"
                        >
                            Home
                        </Label>
                        <Label
                            variant="subtoggle"
                            className="mb-0 cursor-pointer pb-0"
                        >
                            /
                        </Label>
                    </div>
                </RevalidateLink>
                <IconGripVertical className="invisible shrink-0" />
            </div>
            <div className="relative z-20 flex w-full flex-col gap-2">
                {pages &&
                    pages.map((page, index) => {
                        return (
                            <div
                                key={page.id}
                                className={`group relative z-20 flex items-center gap-2 rounded-md border bg-card p-2 transition-all duration-200 hover:bg-muted ${pathname.includes(page.slug) ? "border-primary" : ""}`}
                                // active: border-primary
                            >
                                {/* <IconCircleFilled
                                    size={12}
                                    className={`absolute left-1 top-1 z-10 ${pathname.includes(page.slug) ? "text-primary" : "hidden"}`}
                                /> */}
                                <RevalidateLink
                                    className="relative w-full"
                                    href={`/admin/content/${page.slug}`}
                                >
                                    <div className="flex flex-col">
                                        <Label
                                            variant="toggle"
                                            className="mb-0 flex cursor-pointer items-center pb-0 text-base font-bold"
                                        >
                                            {page.name}
                                        </Label>
                                        <Label
                                            variant="subtoggle"
                                            className="mb-0 cursor-pointer pb-0"
                                        >
                                            /{page.slug}
                                        </Label>
                                    </div>
                                </RevalidateLink>
                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => movePage(index, "up")}
                                        disabled={index === 0}
                                        className="group-hover:bg-accent"
                                    >
                                        <IconArrowUp />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={() => movePage(index, "down")}
                                        disabled={index === pages.length - 1}
                                        className="group-hover:bg-accent"
                                    >
                                        <IconArrowDown />
                                    </Button>
                                </div>
                                <EditPagePopover
                                    key={page.id}
                                    page={page}
                                    isActive={pathname.includes(page.slug)}
                                    user={user}
                                    setNewPages={setNewPages}
                                    pages={pages}
                                    impersonate={impersonate}
                                />
                            </div>
                        );
                    })}
            </div>
        </div>
    );
};
